/*
 * Copyright 2026 Sony Corporation
 */
import type { Platform } from '@ips-jit/schema';
import JSZip from 'jszip';

/** The bytes of a blob; through a FileReader where Blob has no arrayBuffer (older browsers, jsdom). */
export const blobBytes = (blob: Blob): Promise<ArrayBuffer> =>
  typeof blob.arrayBuffer === 'function'
    ? blob.arrayBuffer()
    : new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.onerror = () => reject(reader.error ?? new Error('Could not read the download.'));
        reader.readAsArrayBuffer(blob);
      });

const toBytes = async (data: Blob | ArrayBuffer | Uint8Array): Promise<ArrayBuffer | Uint8Array> =>
  data instanceof Blob ? blobBytes(data) : data;

/**
 * Puts the runtime's credentials into the agent package, so one zip holds all the PC needs: the agent, its install
 * scripts, config.json and the certificates. Both zips hold the same `jit-agent/` folder; file modes are kept, so
 * the binary stays executable and the private key private.
 */
export const combineBundle = async (
  agentPackage: Blob | ArrayBuffer | Uint8Array,
  credentials: Blob | ArrayBuffer | Uint8Array
): Promise<Blob> => {
  const bundle = await JSZip.loadAsync(await toBytes(agentPackage));
  const issued = await JSZip.loadAsync(await toBytes(credentials));
  const files = Object.values(issued.files).filter((file) => !file.dir);
  for (const file of files) {
    bundle.file(file.name, await file.async('uint8array'), {
      date: file.date,
      ...(typeof file.unixPermissions === 'number' ? { unixPermissions: file.unixPermissions } : {}),
    });
  }
  const bytes = await bundle.generateAsync({ type: 'uint8array', compression: 'DEFLATE', platform: 'UNIX' });
  return new Blob([bytes as BlobPart], { type: 'application/zip' });
};

/** "Studio PC" for Windows as jit-agent-studio-pc-windows.zip. */
export const bundleFilename = (runtimeName: string, os: Platform): string => {
  const slug = runtimeName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return `jit-agent-${slug || 'runtime'}-${os}.zip`;
};

/** What to do with the download once it is on the PC. */
export const installHint = (os: Platform): string =>
  os === 'windows'
    ? 'Unzip it on the Windows PC and double-click install.cmd in the jit-agent folder.'
    : 'Unzip it on the Linux PC and run ./install.sh in the jit-agent folder.';
